import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { UploadCloud, Trash2, LogOut, Film, Image as ImageIcon, Inbox, Pencil, RefreshCw, Check, X } from "lucide-react";
import { CATEGORIES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vf_admin_token";

const PORTFOLIO_CATS = CATEGORIES;
const inputCls = "bg-[#0d0d0d] border-white/15 text-[#F5F5F0] h-12 rounded-sm focus-visible:ring-0 focus-visible:border-[#D4AF37] placeholder:text-[#71717a]";

// Upload par blocs : contourne les timeouts d'ingress (typ. 60 s) pour les
// gros fichiers (>1 min). Le fichier est envoyé en tranches de 4 Mo, chaque
// requête HTTP courte (<10 s). Le serveur reconstitue le fichier à l'identique
// (aucun réencodage, aucune compression).
const CHUNK_SIZE = 4 * 1024 * 1024; // 4 Mo
async function chunkedUpload({ file, completeUrl, completeFields, auth, onProgress }) {
  // 1) init
  const initFd = new FormData();
  initFd.append("filename", file.name);
  initFd.append("total_size", String(file.size));
  const { data: initData } = await axios.post(`${API}/admin/upload/init`, initFd, {
    headers: auth.headers, timeout: 30000,
  });
  const sessionId = initData.session_id;
  const chunkSize = initData.chunk_size || CHUNK_SIZE;

  // 2) chunks
  const total = file.size;
  let sent = 0;
  const nb = Math.max(1, Math.ceil(total / chunkSize));
  for (let i = 0; i < nb; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, total);
    const blob = file.slice(start, end);
    const cfd = new FormData();
    cfd.append("session_id", sessionId);
    cfd.append("chunk_index", String(i));
    cfd.append("chunk", blob, `chunk-${i}`);
    // Retry léger : jusqu'à 3 tentatives par bloc pour tolérer les micro-coupures 4G
    let attempt = 0;
    while (true) {
      try {
        await axios.post(`${API}/admin/upload/chunk`, cfd, {
          headers: auth.headers,
          timeout: 60000,
          onUploadProgress: (p) => {
            if (onProgress) {
              const overall = Math.round(((sent + p.loaded) / total) * 99);
              onProgress(Math.min(99, overall));
            }
          },
        });
        break;
      } catch (e) {
        attempt += 1;
        if (attempt >= 3) throw e;
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }
    sent += (end - start);
    if (onProgress) onProgress(Math.min(99, Math.round((sent / total) * 99)));
  }

  // 3) complete
  const dfd = new FormData();
  dfd.append("session_id", sessionId);
  for (const [k, v] of Object.entries(completeFields || {})) dfd.append(k, v);
  const { data } = await axios.post(completeUrl, dfd, {
    headers: auth.headers, timeout: 120000,
  });
  if (onProgress) onProgress(100);
  return data;
}

// Convertit toujours un detail d'erreur API (string, array d'objets 422, objet) en chaîne affichable
const formatApiErrorDetail = (detail, fallback = "Une erreur est survenue.") => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ") || fallback;
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return fallback;
};

const MediaCard = ({ m, auth, categories, onChanged, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(m.title || "");
  const [editCategory, setEditCategory] = useState(m.category || categories[0]);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputId = `admin-replace-input-${m.id}`;

  const label = m.slot === "hero" ? "Showreel" : m.slot === "about" ? "Présentation" : m.category;

  const saveEdit = async () => {
    setSaving(true);
    try {
      const body = { title: editTitle };
      if (m.slot === "portfolio") body.category = editCategory;
      await axios.put(`${API}/admin/media/${m.id}`, body, auth);
      toast.success("Média modifié");
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Échec de la modification"));
    } finally {
      setSaving(false);
    }
  };

  const doReplace = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setReplacing(true);
    setProgress(0);
    try {
      await chunkedUpload({
        file: f,
        completeUrl: `${API}/admin/upload/complete-replace/${m.id}`,
        completeFields: {},
        auth,
        onProgress: setProgress,
      });
      toast.success("Vidéo remplacée");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Échec du remplacement"));
    } finally {
      setReplacing(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid={`admin-media-${m.id}`} className="bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden">
      <div className="aspect-video bg-black grid place-items-center overflow-hidden relative">
        {m.kind === "video" ? (
          <video className="w-full h-full object-cover" src={`${m.url}#t=0.1`} muted playsInline preload="none" poster={m.poster || undefined} />
        ) : (
          <img src={m.url} alt={m.title} className="w-full h-full object-cover" />
        )}
        {replacing && (
          <div className="absolute inset-0 bg-black/70 grid place-items-center text-[#D4AF37] text-sm">
            Remplacement… {progress}%
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
          {m.kind === "video" ? <Film size={14} /> : <ImageIcon size={14} />}
          <span className="text-[9px] uppercase tracking-[0.2em]">{label}</span>
        </div>

        {m.conversion_error && (
          <div data-testid={`admin-conversion-error-${m.id}`} className="mb-3 p-2 border border-red-400/40 bg-red-500/10 rounded-sm">
            <p className="text-[10px] uppercase tracking-[0.15em] text-red-300 mb-1">Erreur de conversion .mov → .mp4</p>
            <p className="text-[11px] text-red-100 break-words">{m.conversion_error}</p>
            {m.conversion_error_at && <p className="text-[9px] text-red-200/60 mt-1">{new Date(m.conversion_error_at).toLocaleString('fr-FR')}</p>}
          </div>
        )}

        {editing ? (
          <div className="space-y-3">
            <Input
              data-testid={`admin-edit-title-${m.id}`}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className={inputCls}
              placeholder="Titre"
            />
            {m.slot === "portfolio" && (
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger data-testid={`admin-edit-category-${m.id}`} className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0d0d0d] border-white/15 text-[#F5F5F0]">
                  {categories.map((c) => <SelectItem key={c} value={c} className="focus:bg-[#D4AF37] focus:text-black">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-2">
              <button data-testid={`admin-save-${m.id}`} onClick={saveEdit} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-black rounded-full px-4 py-1.5 text-xs font-medium disabled:opacity-60">
                <Check size={13} /> Enregistrer
              </button>
              <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 border border-white/15 text-[#A1A1AA] rounded-full px-4 py-1.5 text-xs">
                <X size={13} /> Annuler
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#F5F5F0] truncate mb-3">{m.title || "Sans titre"}</p>
            <div className="flex flex-wrap gap-2">
              <button data-testid={`admin-edit-${m.id}`} onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 border border-white/15 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 rounded-full px-3 py-1.5 text-xs transition-colors">
                <Pencil size={13} /> Modifier
              </button>
              <label htmlFor={inputId} data-testid={`admin-replace-${m.id}`} className="inline-flex items-center gap-1.5 border border-white/15 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 rounded-full px-3 py-1.5 text-xs cursor-pointer transition-colors">
                <RefreshCw size={13} /> Remplacer
              </label>
              <input id={inputId} type="file" accept="video/mp4,video/quicktime,video/webm,image/*" onChange={doReplace} className="hidden" />
              <button data-testid={`admin-delete-${m.id}`} onClick={() => onDelete(m.id)} className="inline-flex items-center gap-1.5 border border-white/15 text-[#A1A1AA] hover:text-red-400 hover:border-red-400/50 rounded-full px-3 py-1.5 text-xs transition-colors">
                <Trash2 size={13} /> Supprimer
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Merci de saisir votre email et votre mot de passe.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success("Connexion réussie");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Identifiants incorrects"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] px-6">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md bg-[#0a0a0a] border border-white/10 p-10 rounded-sm">
        <p className="eyebrow mb-3">Espace privé</p>
        <h1 className="font-serif text-4xl text-[#F5F5F0] mb-8">VF Création — Admin</h1>
        <div className="space-y-5">
          <div>
            <Label className="text-[#A1A1AA] text-xs uppercase tracking-wide">Email</Label>
            <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`mt-2 ${inputCls}`} placeholder="admin@vfcreation.fr" />
          </div>
          <div>
            <Label className="text-[#A1A1AA] text-xs uppercase tracking-wide">Mot de passe</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`mt-2 ${inputCls}`} placeholder="••••••••" />
          </div>
          <button data-testid="admin-login-btn" type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-black rounded-full py-3 text-sm font-medium tracking-wide hover:bg-[#F3E19B] transition-colors disabled:opacity-60">
            {loading ? "Connexion..." : "Se connecter"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [media, setMedia] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("media");

  const [file, setFile] = useState(null);
  const [slot, setSlot] = useState("portfolio");
  const [category, setCategory] = useState("Immobilier");
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = useCallback(async () => {
    try {
      const [m, q] = await Promise.all([
        axios.get(`${API}/media`),
        axios.get(`${API}/admin/quotes`, auth),
      ]);
      setMedia(m.data);
      setQuotes(q.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    setUploading(true);
    setProgress(0);
    try {
      await chunkedUpload({
        file,
        completeUrl: `${API}/admin/upload/complete`,
        completeFields: {
          slot,
          category: slot === "portfolio" ? category : "showreel",
          title: title || file.name,
        },
        auth,
        onProgress: setProgress,
      });
      toast.success("Média ajouté");
      setFile(null); setTitle("");
      document.getElementById("admin-file-input").value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail, "Échec de l'upload"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce média du site ?")) return;
    try {
      await axios.delete(`${API}/admin/media/${id}`, auth);
      toast.success("Média supprimé");
      load();
    } catch (err) {
      toast.error("Échec de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0]">
      <header className="border-b border-white/10 px-6 lg:px-10 h-20 flex items-center justify-between sticky top-0 bg-black/70 backdrop-blur-xl z-10">
        <span className="font-serif text-2xl gold-text">VF Création — Admin</span>
        <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">
          <LogOut size={16} /> Déconnexion
        </button>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex gap-3 mb-10">
          <button data-testid="tab-media" onClick={() => setTab("media")} className={`rounded-full px-5 py-2 text-sm border transition-colors ${tab === "media" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-[#A1A1AA]"}`}>Médias</button>
          <button data-testid="tab-quotes" onClick={() => setTab("quotes")} className={`rounded-full px-5 py-2 text-sm border transition-colors ${tab === "quotes" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-[#A1A1AA]"}`}>Demandes de devis ({quotes.length})</button>
        </div>

        {tab === "media" && (
          <>
            <form data-testid="admin-upload-form" onSubmit={upload} className="bg-[#0a0a0a] border border-white/10 p-8 rounded-sm mb-10 grid lg:grid-cols-12 gap-5 items-end">
              <div className="lg:col-span-4">
                <Label className="text-[#A1A1AA] text-xs uppercase tracking-wide">Fichier (.mp4, .mov, image)</Label>
                <input id="admin-file-input" data-testid="admin-file-input" type="file" accept="video/mp4,video/quicktime,video/webm,image/*" onChange={(e) => setFile(e.target.files[0])} className="mt-2 w-full text-sm text-[#A1A1AA] file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:bg-[#D4AF37] file:text-black file:text-sm file:cursor-pointer" />
              </div>
              <div className="lg:col-span-3">
                <Label className="text-[#A1A1AA] text-xs uppercase tracking-wide">Emplacement</Label>
                <Select value={slot} onValueChange={setSlot}>
                  <SelectTrigger data-testid="admin-slot" className={`mt-2 ${inputCls}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0d0d0d] border-white/15 text-[#F5F5F0]">
                    <SelectItem value="hero" className="focus:bg-[#D4AF37] focus:text-black">Accueil / Showreel</SelectItem>
                    <SelectItem value="about" className="focus:bg-[#D4AF37] focus:text-black">Vidéo de présentation</SelectItem>
                    <SelectItem value="portfolio" className="focus:bg-[#D4AF37] focus:text-black">Portfolio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {slot === "portfolio" && (
                <div className="lg:col-span-3">
                  <Label className="text-[#A1A1AA] text-xs uppercase tracking-wide">Catégorie</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="admin-category" className={`mt-2 ${inputCls}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0d0d0d] border-white/15 text-[#F5F5F0]">
                      {PORTFOLIO_CATS.map((c) => <SelectItem key={c} value={c} className="focus:bg-[#D4AF37] focus:text-black">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className={slot === "portfolio" ? "lg:col-span-12" : "lg:col-span-5"}>
                <Label className="text-[#A1A1AA] text-xs uppercase tracking-wide">Titre</Label>
                <Input data-testid="admin-title" value={title} onChange={(e) => setTitle(e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Titre de la réalisation" />
              </div>
              <div className="lg:col-span-12">
                <button data-testid="admin-upload-btn" type="submit" disabled={uploading} className="inline-flex items-center gap-3 bg-[#D4AF37] text-black rounded-full px-7 py-3 text-sm font-medium tracking-wide hover:bg-[#F3E19B] transition-colors disabled:opacity-60">
                  <UploadCloud size={18} /> {uploading ? `Envoi... ${progress}%` : "Téléverser"}
                </button>
              </div>
            </form>

            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="font-serif text-3xl">Médias en ligne ({media.length})</h2>
              <button
                data-testid="admin-regen-thumbnails"
                type="button"
                onClick={async () => {
                  if (!window.confirm("Régénérer les aperçus vidéo (.mov → .mp4 + version web) ? L'opération peut durer plusieurs minutes.")) return;
                  toast.info("Régénération lancée. Merci de patienter…");
                  try {
                    const { data } = await axios.post(`${API}/admin/convert-videos`, {}, { headers: auth.headers, timeout: 600000 });
                    const s = data.summary || {};
                    toast.success(`Terminé — ${s.web_generated || 0} aperçu(s) régénéré(s), ${s.converted_mp4 || 0} vidéo(s) converti(es), ${s.errors || 0} erreur(s).`);
                    load();
                  } catch (err) {
                    if (err.response?.status === 409) toast.error("Une régénération est déjà en cours");
                    else toast.error(formatApiErrorDetail(err.response?.data?.detail, "Échec de la régénération"));
                  }
                }}
                className="inline-flex items-center gap-2 text-sm border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors rounded-full px-4 py-2"
              >
                <RefreshCw size={14} /> Régénérer les aperçus
              </button>
            </div>
            {media.length === 0 && <p className="text-[#A1A1AA]">Aucun média. Téléversez vos vidéos pour remplacer les démonstrations du site.</p>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {media.map((m) => (
                <MediaCard
                  key={m.id}
                  m={m}
                  auth={auth}
                  categories={PORTFOLIO_CATS}
                  onChanged={load}
                  onDelete={remove}
                />
              ))}
            </div>
          </>
        )}

        {tab === "quotes" && (
          <div>
            <h2 className="font-serif text-3xl mb-6 flex items-center gap-3"><Inbox size={24} className="text-[#D4AF37]" /> Demandes de devis</h2>
            {quotes.length === 0 && <p className="text-[#A1A1AA]">Aucune demande pour le moment.</p>}
            <div className="space-y-4">
              {quotes.map((q) => (
                <div key={q.id} data-testid={`quote-${q.id}`} className="bg-[#0a0a0a] border border-white/10 rounded-sm p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div>
                      <span className="text-lg text-[#F5F5F0]">{q.name}</span>
                      <span className="text-[#A1A1AA] text-sm ml-3">{q.email}</span>
                    </div>
                    <span className="text-xs text-[#71717a]">{new Date(q.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <div className="text-sm text-[#A1A1AA] flex flex-wrap gap-x-6 gap-y-1 mb-3">
                    {q.phone && <span>Tél : {q.phone}</span>}
                    {q.company && <span>Entreprise : {q.company}</span>}
                    {q.sector && <span>Secteur : {q.sector}</span>}
                    {q.project_type && <span>Projet : {q.project_type}</span>}
                  </div>
                  <p className="text-[#F5F5F0] bg-[#050505] border border-white/10 rounded p-4 text-sm">{q.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => logout());
  }, [token]);

  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
};

export default Admin;
